import { useState } from "react";
import { Modal } from "./Modal";

type Kind = "bug" | "feature";
type Priority = "high" | "med" | "low";

// In-app bug/feature reporter.  Single POST to /api/reports; we keep the
// modal open on failure (showing a small error) so the user doesn't lose
// what they typed, and close on success after a brief "Sent" confirmation.

export function BugReportModal({
  accessToken,
  onClose,
  onSubmitted,
}: {
  accessToken: string;
  onClose: () => void;
  /** Fired after a successful POST, before the brief "Sent" pause —
   *  the parent can refresh its list immediately so the new row is
   *  visible the moment the modal closes. */
  onSubmitted?: () => void;
}) {
  const [kind, setKind] = useState<Kind>("bug");
  const [priority, setPriority] = useState<Priority>("med");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/reports", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          kind,
          priority,
          title: title.trim(),
          body: body.trim(),
        }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => null)) as { error?: string } | null;
        throw new Error(j?.error ?? `HTTP ${r.status}`);
      }
      setSent(true);
      onSubmitted?.();
      setTimeout(onClose, 700);
    } catch (err) {
      setError(err instanceof Error ? err.message : "submit_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Report a bug or suggest a feature" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3 text-sm">
        <div>
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Type
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 dark:border-slate-600">
            <KindButton current={kind} value="bug" onSelect={setKind}>
              🐞 Bug
            </KindButton>
            <KindButton current={kind} value="feature" onSelect={setKind}>
              ✨ Feature
            </KindButton>
          </div>
        </div>

        <div>
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Priority
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 dark:border-slate-600">
            <PriorityButton current={priority} value="high" onSelect={setPriority}>
              High
            </PriorityButton>
            <PriorityButton current={priority} value="med" onSelect={setPriority}>
              Med
            </PriorityButton>
            <PriorityButton current={priority} value="low" onSelect={setPriority}>
              Low
            </PriorityButton>
          </div>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Title
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder={
              kind === "bug"
                ? "What went wrong?"
                : "What would you like to see?"
            }
            className="rounded-md border border-slate-300 bg-white px-2 py-1 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800"
            required
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Details <span className="text-slate-400 normal-case dark:text-slate-500">(optional)</span>
          </span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={5000}
            placeholder={
              kind === "bug"
                ? "Steps to reproduce, what you expected, what happened…"
                : "How would you use it? What problem does it solve?"
            }
            className="resize-y rounded-md border border-slate-300 bg-white px-2 py-1 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        {error && (
          <div className="rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-300">
            Couldn't send: {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          {sent && (
            <span className="mr-auto text-xs text-emerald-700 dark:text-emerald-300">
              Sent — thanks!
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!title.trim() || busy || sent}
            className="rounded-md bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
          >
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function KindButton({
  current,
  value,
  onSelect,
  children,
}: {
  current: Kind;
  value: Kind;
  onSelect: (v: Kind) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={active}
      className={`px-3 py-1 text-sm ${
        active
          ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
          : "bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function PriorityButton({
  current,
  value,
  onSelect,
  children,
}: {
  current: Priority;
  value: Priority;
  onSelect: (v: Priority) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  // Subtle colour cue per priority when active so the row is easier to
  // scan at a glance — neutral when inactive to avoid a rainbow row.
  const activeCls =
    value === "high"
      ? "bg-rose-600 text-white"
      : value === "med"
        ? "bg-amber-500 text-white"
        : "bg-slate-500 text-white";
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={active}
      className={`px-3 py-1 text-sm ${
        active ? activeCls : "bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}
